import { useRef, useState } from 'react';
import { BOOK_IMAGE_PLACEHOLDER } from '../../utils/bookImage';

// Amazon-style hover zoom.
// The lens has the same aspect ratio as the preview panel (lens = panel/zoom),
// and the panel's background-position is the lens top-left × zoom in pixels.
// This guarantees the panel is always fully covered by the magnified image —
// no edge of the panel is ever blank.
const ImageZoom = ({ src, alt, zoom = 2.5, panelWidth = 420, panelHeight = 480 }) => {
  const imgRef = useRef(null);
  const [failedSource, setFailedSource] = useState(null);
  const displaySource = failedSource === src ? BOOK_IMAGE_PLACEHOLDER : src;
  const [hovering, setHovering] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ w: 0, h: 0 });

  // Derive lens dimensions from the panel + zoom so aspect ratios match.
  // Cap to the source image size in case the panel is bigger than image*zoom.
  let lensW = panelWidth / zoom;
  let lensH = panelHeight / zoom;
  if (size.w > 0) {
    lensW = Math.min(lensW, size.w);
    lensH = Math.min(lensH, size.h);
  }

  const handleEnter = () => {
    const rect = imgRef.current?.getBoundingClientRect();
    if (rect) setSize({ w: rect.width, h: rect.height });
    setHovering(true);
  };

  const handleMove = (e) => {
    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect) return;
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;
    // clamp so the lens stays fully inside the image
    x = Math.max(lensW / 2, Math.min(rect.width - lensW / 2, x));
    y = Math.max(lensH / 2, Math.min(rect.height - lensH / 2, y));
    setPos({ x, y });
  };

  const lensLeft = pos.x - lensW / 2;
  const lensTop = pos.y - lensH / 2;

  return (
    <div
      className="relative w-full"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setHovering(false)}
      onMouseMove={handleMove}
    >
      <img
        ref={imgRef}
        src={displaySource}
        onError={() => setFailedSource(src)}
        alt={alt}
        draggable={false}
        className="w-full h-auto object-cover block select-none"
      />

      {/* Lens highlight on the source image */}
      {hovering && size.w > 0 && (
        <span
          aria-hidden="true"
          className="hidden md:block absolute pointer-events-none border border-gray-400 bg-yellow-200/30"
          style={{
            left: `${lensLeft}px`,
            top: `${lensTop}px`,
            width: `${lensW}px`,
            height: `${lensH}px`,
          }}
        />
      )}

      {/* Magnified preview panel — fully covered by the zoomed source */}
      {hovering && size.w > 0 && (
        <div
          aria-hidden="true"
          className="hidden md:block absolute top-0 z-40 pointer-events-none bg-white border border-gray-200 shadow-2xl overflow-hidden"
          style={{
            left: `calc(100% + 16px)`,
            width: `${panelWidth}px`,
            height: `${panelHeight}px`,
            backgroundImage: `url("${displaySource}")`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: `${size.w * zoom}px ${size.h * zoom}px`,
            backgroundPosition: `-${lensLeft * zoom}px -${lensTop * zoom}px`,
          }}
        />
      )}
    </div>
  );
};

export default ImageZoom;
